import { DatePipe } from '@angular/common';
import { Component, ComponentFactoryResolver, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { visualizacaoModoList } from '../../../../../common/visualizacao-modo';
import { ApiError, AuthService, BaseVisualizacaoService, DataTablesService, MessageService, VisualizacaoStorageService } from '../../../../../core';
import { CurrencyFormatPipe, DecimalFormatPipe } from '../../../../../shared';
import { UsuarioPerfil } from '../../../../usuario/usuario-perfil';
import { CampanhaAnaliseAuthGuardService, CampanhaAnaliseHttpService } from '../../analise';
import { CampanhaEnviarAnaliseFormModalComponent } from '../../analise/enviar-analise-form-modal';
import { CampanhaOptions, CampanhaOptionsActions } from '../../campanha-options';
import { CampanhaSearch, CampanhaSearchTotal } from '../../campanha-search';
import { CampanhaSearchOrderBy } from '../../campanha-search-orderby';
import { CampanhaEncerramentoAuthGuardService, CampanhaEncerramentoHttpService } from '../../encerramento';
import { CampanhaFilter } from '../../filter';
import { CampanhaAuthGuardService, CampanhaCrudHttpService, CampanhaSearchHttpService } from '../../services';
import { CampanhaPedidoAuthGuardService } from '../../pedidos/services';
import { campanhaStatusEmAnalise } from '../../status';
import { CampanhaStatusBadgeComponent } from '../../status/badge';
import { CampanhaVisualizarModalComponent } from '../../visualizar-modal';


@Component({
    selector: 'sgt-campanha-table',
    templateUrl : './campanha-table.component.html',
    styleUrls: ['./campanha-table.component.less']
})
export class CampanhaTableComponent implements OnInit, OnDestroy {
    perfilUsuario: UsuarioPerfil;
    idUsuario: number;
    campanhaEditing: CampanhaSearch;

    @Input() visualizacaoStorageKey = 'campanhaTableVisualizacao';
    @Input() filter: CampanhaFilter;
    @Input() optionsVisibility: CampanhaOptionsActions = {
        canEdit: true,
        canVisualizar: true,
        canDelete: true,
        // Análise
        canSolicitarAnalise: true,
        canAprovarAnalise: true,
        canReprovarAnalise: true,
        // Encerramento
        canEncerrar: true,
        // Pedidos
        canLiberarPedidos: true
    }

    // Total
    isLoadingTotal: boolean;
    total: CampanhaSearchTotal;

    private dataTable: any;
    /** Subscriptions das pesquisas. Utilizado para realizar o unsubscribe e cancelar o retorno de requests de pesquissa pendentes antes de iniciar novos requests */
    private searchSubs$: Subscription;

    /** Container para criar dinamicamente o badge do status */
    @ViewChild('badgeStatusContainer', {read: ViewContainerRef}) badgeStatusContainer: ViewContainerRef;
    // Análise
    @ViewChild('enviarAnaliseFormModal') enviarAnaliseFormModal: CampanhaEnviarAnaliseFormModalComponent;
    // Visualizar Campanha
    @ViewChild('visualizarModal') visualizarModal: CampanhaVisualizarModalComponent;

    constructor(private el: ElementRef,
                private activatedRoute: ActivatedRoute,
                private componentFactoryResolver: ComponentFactoryResolver,
                private renderer: Renderer2,
                private datePipe: DatePipe,
                private currencyFormatPipe: CurrencyFormatPipe,
                private decimalFormatPipe: DecimalFormatPipe,
                private authService: AuthService,
                private baseVisualizacaoService: BaseVisualizacaoService,
                private dataTablesService: DataTablesService,
                private messageService: MessageService,
                private visualizacaoStorageService: VisualizacaoStorageService,
                private campanhaAuthGuardService: CampanhaAuthGuardService,
                private campanhaSearchHttpService: CampanhaSearchHttpService,
                private campanhaCrudHttpService: CampanhaCrudHttpService,
                private campanhaAnaliseAuthGuardService: CampanhaAnaliseAuthGuardService,
                private campanhaAnaliseHttpService: CampanhaAnaliseHttpService,
                private campanhaEncerramentoAuthGuardService: CampanhaEncerramentoAuthGuardService,
                private campanhaEncerramentoHttpService: CampanhaEncerramentoHttpService,
                private campanhaPedidoAuthGuardService: CampanhaPedidoAuthGuardService) { }

    ngOnInit(): void {
        this.perfilUsuario = this.authService.getPerfilUsuario();
        this.idUsuario = this.authService.getIdUsuario();
    }

    search() {
        // Realizando unsubscribe dos subscriptions atuais e criando um novo para as próximas pesquisas
        this.searchSubs$?.unsubscribe();
        this.searchSubs$ = new Subscription();

        // Obtendo os totais das campanhas
        this.getTotais()
            .then(() => {
                if (!this.dataTable) { // Table ainda não criada
                    // Obtendo os dados da datatable armazenados no storage para sabermos o order e posição do scroll
                    const vs = this.visualizacaoStorageService.get(this.visualizacaoStorageKey);

                    this.createTable(vs?.order)
                        .then(() => {
                            // Realizando scroll para a posição inicial salva
                            if (vs?.scrollerStartPosition > this.dataTable?.scroller?.page()?.end) {
                                this.dataTable?.scroller?.toPosition(vs?.scrollerStartPosition);
                            }
                        });
                } else { // Table já criada, somente damos um reload
                    this.dataTable.ajax.reload();
                }
            })
            .catch(() => {});
    }

    /** Obtem os totais das campanhas */
    private getTotais(): Promise<CampanhaSearchTotal> {
        return new Promise<CampanhaSearchTotal>((resolve, reject) => {
            this.searchSubs$.add(this.campanhaSearchHttpService.getTotais(this.filter)
                .subscribe(
                    total => {
                        this.total = total;
                        this.isLoadingTotal = false;
                        resolve(total);
                    },
                    (error: ApiError) => {
                        this.isLoadingTotal = false;
                        this.messageService.error(error.message, 'Erro ao obter os totais das campanhas', error);
                        reject();
                    }
                )
            );

        })
    }

    /** Cria a dataTable das campanhas */
    private createTable(initOrder: any[] = null): Promise<void> {
        return new Promise<void>((resolve) => {
            const columns: any[] = [
                {
                    data: null,
                    className: 'col-options',
                    orderable: false,
                    render: (data, type, row, meta) => {
                        const entity = data as CampanhaSearch;

                        // Opções
                        const options = new CampanhaOptions();
                        // Editar
                        options.canEdit = this.optionsVisibility.canEdit && this.campanhaAuthGuardService.canEdit(entity.id, entity.status, entity.idUsuarioCreated);
                        // Visualizar
                        options.canVisualizar = this.optionsVisibility.canVisualizar && this.campanhaAuthGuardService.canVisualizar();

                        /** Análise */
                        // Solicitar Análise
                        options.canSolicitarAnalise = this.optionsVisibility.canSolicitarAnalise && this.campanhaAnaliseAuthGuardService.canSolicitar(entity.id, entity.status, entity.idUsuarioCreated);
                        // Aprovar Análise
                        options.canAprovarAnalise = this.optionsVisibility.canAprovarAnalise && this.campanhaAnaliseAuthGuardService.canEnviar(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0);
                        // Reprovar Análise
                        options.canReprovarAnalise = this.optionsVisibility.canReprovarAnalise && this.campanhaAnaliseAuthGuardService.canEnviar(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0);

                        /** Encerramento */
                        // Encerrar
                        options.canEncerrar = this.optionsVisibility.canEncerrar && this.campanhaEncerramentoAuthGuardService.canEncerrar(entity.id, entity.status);

                        /** Pedidos */
                        // Liberar Pedidos
                        options.canLiberarPedidos = this.optionsVisibility.canLiberarPedidos && this.campanhaPedidoAuthGuardService.canLiberarPedidos(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0);

                        // Excluir
                        options.canDelete = this.optionsVisibility.canDelete && this.campanhaAuthGuardService.canDelete(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0, entity.idUsuarioCreated);

                        let template = '';
                        if (options.hasAnyOption()) {
                            template += `
                                <span class="btn-group dropup table-options">
                                    <button type="button" title="Opções" class="btn btn-link btn-options dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-campanha">
                            `;
                            // Editar
                            if (options.canEdit) {
                                template +=  `
                                    <a class="dropdown-item" id="edit">
                                        <i class="fas fa-pencil-alt" aria-hidden="true"></i> Editar
                                    </a>
                                `;
                            }

                            // Visualizaar
                            if (options.canVisualizar) {
                                template +=  `
                                    <a class="dropdown-item" id="visualizar">
                                        <i class="fas fa-search"></i> Visualizar
                                    </a>
                                `;
                            }

                            /** Análise */
                            if (options.hasAnyOptionAnalise()) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <h6 class="dropdown-header">Análise</h6>
                                `;

                                // Solicitar Análise
                                if (options.canSolicitarAnalise) {
                                    template +=  `
                                        <a class="dropdown-item dropdown-item-campanha-solicitar-analise" id="solicitarAnalise">
                                            <i class="fas fa-gavel"></i> Solicitar Análise
                                        </a>
                                    `;
                                }
                                // Aprovar Análise
                                if (options.canAprovarAnalise) {
                                    template +=  `
                                        <a class="dropdown-item dropdown-item-campanha-aprovar-analise" id="aprovarAnalise">
                                            <i class="far fa-thumbs-up"></i> Aprovar Campanha
                                        </a>
                                    `;
                                }
                                // Reprovar Análise
                                if (options.canReprovarAnalise) {
                                    template +=  `
                                        <a class="dropdown-item dropdown-item-campanha-reprovar-analise" id="reprovarAnalise">
                                            <i class="far fa-thumbs-down"></i> Reprovar Campanha
                                        </a>
                                    `;
                                }
                            }

                            /** Encerramento */
                            if (options.hasAnyOptionEncerramento()) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <h6 class="dropdown-header">Encerramento</h6>
                                `;

                                // Encerrar
                                if (options.canEncerrar) {
                                    template +=  `
                                        <a class="dropdown-item dropdown-item-campanha-encerrar" id="encerrar">
                                            <i class="fas fa-check-double"></i> Encerrar Campanha
                                        </a>
                                    `;
                                }
                            }

                            /** Pedidos */
                            if (options.hasAnyOptionPedido()) {
                                template += `
                                    <div class="dropdown-divider"></div>
                                    <h6 class="dropdown-header">Pedidos</h6>
                                `;

                                // Liberar Pedidos
                                if (options.canLiberarPedidos) {
                                    template +=  `
                                        <a class="dropdown-item dropdown-item-warning" id="liberarPedidos">
                                            <i class="far fa-thumbs-up"></i> Abrir Liberação de Pedido(s)
                                        </a>
                                    `;
                                }
                            }


                            // Excluir
                            if (options.canDelete) {
                                template +=  `
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item dropdown-item-delete" id="delete">
                                        <i class="far fa-trash-alt" aria-hidden="true"></i> Excluir
                                    </a>
                                `;
                            }

                            template += `
                                    </div>
                                </span>
                            `;
                        }

                        return template;
                    },
                    createdCell: (td, cellData, rowData, row, col) => {
                        const dropdown = jQuery(td).find('.btn-group');
                        // Ao abrir o dropdown, adicionamos ele no body para exibi-lo corretamente na table
                        dropdown.on('show.bs.dropdown', function() {
                            jQuery('body').append(dropdown.css({
                                    position: 'absolute',
                                    left: dropdown.offset().left,
                                    top: dropdown.offset().top
                                }).detach());
                        });
                        // Ai fechar o dropdown, adicionamos ele novamente a coluna das opções na table
                        dropdown.on('hidden.bs.dropdown', function() {
                            jQuery(td).append(dropdown.css({
                                position: 'relative',
                                left: 'auto',
                                top: 'auto'
                            }).detach());
                        });

                        // Vinculando os eventos de click das opções da tabela
                        const myself = this;
                        // Opções do dropdown
                        dropdown.on('click', 'a', function() {
                            if (this.id === 'edit') {
                                myself.edit(cellData);
                            } else if (this.id === 'visualizar') {
                                myself.visualizar(cellData);
                            } else if (this.id === 'solicitarAnalise') {
                                myself.solicitarAnalise(cellData);
                            } else if (this.id === 'aprovarAnalise') {
                                myself.aprovarAnalise(cellData);
                            } else if (this.id === 'reprovarAnalise') {
                                myself.reprovarAnalise(cellData);
                            } else if (this.id === 'encerrar') {
                                myself.encerrar(cellData);
                            } else if (this.id === 'delete') {
                                myself.delete(cellData);
                            } else if (this.id === 'liberarPedidos') {
                                myself.liberarPedidos(cellData);
                            }
                        });
                    }
                },
                {
                    data: 'numero',
                    className: 'col-numero',
                    serverOrderByColumn: CampanhaSearchOrderBy.Numero,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'nome',
                    className: 'col-nome',
                    serverOrderByColumn: CampanhaSearchOrderBy.Nome,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: null,
                    className: 'col-status text-center',
                    serverOrderByColumn: CampanhaSearchOrderBy.Status,
                    render: () => '',
                    createdCell: (td: any, cellData: any, entity: CampanhaSearch) => {
                        if (entity) {
                            const factory = this.componentFactoryResolver.resolveComponentFactory(CampanhaStatusBadgeComponent);
                            const ref = this.badgeStatusContainer.createComponent(factory);
                            ref.instance.status = entity.status;
                            this.renderer.appendChild(td, ref.location.nativeElement);
                        }
                    }
                },
                {
                    data: 'nomeArea',
                    className: 'col-nome-area',
                    serverOrderByColumn: CampanhaSearchOrderBy.NomeArea,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'nomePacoteCampanha',
                    className: 'col-nome-pacote',
                    serverOrderByColumn: CampanhaSearchOrderBy.NomePacoteCampanha,
                    createdCell: (td, cellData, rowData, row, col) => {
                        if (cellData) {
                            td.title = cellData;
                        }
                    }
                },
                {
                    data: 'objetivoVenda',
                    className: 'col-objetivo col-objetivo-venda',
                    serverOrderByColumn: CampanhaSearchOrderBy.ObjetivoVenda,
                    render: (data: boolean) => data ? 'Sim' : 'Não',
                },
                {
                    data: 'objetivoBonificacao',
                    className: 'col-objetivo col-objetivo-bonificacao',
                    serverOrderByColumn: CampanhaSearchOrderBy.ObjetivoBonificacao,
                    render: (data: boolean) => data ? 'Sim' : 'Não',
                },
                {
                    data: 'objetivoAquisicao',
                    className: 'col-objetivo col-objetivo-aquisicao',
                    serverOrderByColumn: CampanhaSearchOrderBy.ObjetivoAquisicao,
                    render: (data: boolean) => data ? 'Sim' : 'Não',
                },
                {
                    data: 'vigenciaRecorrente',
                    className: 'col-vigencia col-vigencia-recorrente',
                    serverOrderByColumn: CampanhaSearchOrderBy.VigenciaRecorrente,
                    render: (data: boolean) => data ? 'Sim' : 'Não',
                },
                {
                    data: 'dataInicioAndamento',
                    className: 'col-vigencia col-data-inicio-vigencia text-center',
                    serverOrderByColumn: CampanhaSearchOrderBy.DataInicioAndamento,
                    render: (data) => this.datePipe.transform(data, 'dd/MM/yyyy - HH:mm'),
                },
                {
                    data: 'dataTerminoVigencia',
                    className: 'col-vigencia col-data-termino-vigencia text-center',
                    serverOrderByColumn: CampanhaSearchOrderBy.DataTerminoVigencia,
                    render: (data) => this.datePipe.transform(data, 'dd/MM/yyyy - HH:mm'),
                },
                {
                    data: null,
                    className: 'col-roi-recursos text-right',
                    serverOrderByColumn: CampanhaSearchOrderBy.ValorTotalRecursos,
                    render: (data: CampanhaSearch) => this.currencyFormatPipe.transform(data?.resultado?.roi?.valorTotalDespesas, 1, 2, 2),
                },
                {
                    data: null,
                    className: 'col-roi-resultado text-right',
                    serverOrderByColumn: CampanhaSearchOrderBy.ValorTotalPedidosVendas,
                    render: (data: CampanhaSearch) => this.currencyFormatPipe.transform(data?.resultado?.roi?.valorTotalVendas, 1, 2, 2),
                },
                {
                    data: null,
                    className: 'col-roi text-right',
                    orderable: false,
                    render: (data: CampanhaSearch) => `${this.decimalFormatPipe.transform(data?.resultado?.roi?.retornoEfetivo, 1, 2, 2)}x`,
                }
            ];

            const columnDefs: any[] = [
                {orderable: false, targets: [0]}
            ];

            this.dataTablesService.showTable('#campanhaTable', this.dataTable, {
                columnDefs,
                columns,
                order: initOrder ? initOrder : [[1, 'asc']], // código asc
                searching: false,
                info: false,
                serverSide: true,
                processing: true,
                deferRender: true,
                scroller: true,
                scrollX: true,
                // stateSave: true,
                ajax: (data, callback, settings) => {
                    // Order By
                    const order = data?.order[0];
                    const orderColumn = columns[order?.column];
                    const orderBy = orderColumn?.serverOrderByColumn;
                    const orderByDir = order?.dir;
                    // Limit
                    const limit = data.length;
                    // Offset
                    const offset = data.start;

                    this.searchSubs$.add(this.campanhaSearchHttpService.search(visualizacaoModoList, null, null, this.filter, orderBy, orderByDir, limit, offset)
                        .subscribe(
                            entities => {
                                callback({recordsTotal: this.total?.quantidade, recordsFiltered: this.total?.quantidade, data: entities});
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, 'Erro ao pesquisar as campanhas', error);
                                callback({recordsTotal: 0, recordsFiltered: 0, data: []});
                            }
                        )
                    );
                }
            }).then(dataTable => {
                this.dataTable = dataTable;
                this.dataTable
                    .on('preDraw', () => { // Antes de redesenhar a table
                        // Destruindo os badges das status
                        this.clearBadgeStatusContainer();
                    });

                setTimeout(() => {
                    // Adicionando evento handler no scroll do body da datatable
                    jQuery(this.el.nativeElement).find('.dataTables_scrollBody').on('scroll', () => {
                        this.visualizacaoStorageService.set(null, null, this.dataTable?.page?.info()?.page, this.dataTable?.order(), this.dataTable?.scroller?.page()?.start, this.visualizacaoStorageKey);
                    });

                    resolve();
                }, 500);
            })
            .catch(() => {});
        });
    }

    /** Atualizando a pesquisa */
    refresh() {
        // Atualizando a pesquisa através da datatable
        this.dataTable?.draw(false);
        // Atualizando os totais
        this.getTotais()
            .then(() => {})
            .catch(() => {});
    }

    /** Destroi todos badges dos status  */
    private clearBadgeStatusContainer() {
        this.badgeStatusContainer?.clear();
    }

    edit(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAuthGuardService.canEdit(entity.id, entity.status, entity.idUsuarioCreated)) {
            this.messageService.info(`Você não possui permissão para editar a campanha ${entity?.nome ?? ''}`, 'Sem permissão para editar');
            return false;
        }

        this.baseVisualizacaoService.edit(entity.id, this.activatedRoute);
    }

    /** Abre o modal para visualizar a campanha */
    visualizar(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }

        this.visualizarModal.open(entity.id, this.filter);
    }

    delete(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAuthGuardService.canDelete(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0, entity.idUsuarioCreated)) {
            this.messageService.info(`Você não possui permissão para excluir a campanha ${entity?.nome ?? ''}`, 'Sem permissão para excluir');
            return false;
        }

        this.messageService.confirm(`Deseja realmente excluir a de campanha ${entity.nome}?`).then(result => {
            if (result) {
                this.campanhaCrudHttpService.delete(entity.id)
                    .subscribe(
                        () => {
                            this.messageService.success('', `Campanha ${entity.nome} excluída com sucesso`);
                            this.refresh();
                        },
                        (error: ApiError) => {
                            this.messageService.error(error.message, error.cause, error);
                        }
                    );
            }
        });
    }

    /** Solicita a análise da campanha */
    solicitarAnalise(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAnaliseAuthGuardService.canSolicitar(entity.id, entity.status, entity.idUsuarioCreated)) {
            this.messageService.info(`Você não possui permissão para solicitar a análise da campanha ${entity?.nome ?? ''}`, 'Sem permissão para solicitar análise');
            return false;
        }

        this.messageService.confirm(`Deseja realmente solicitar a análise da campanha ${entity.nome}?`).then(result => {
            if (result) {
                this.campanhaAnaliseHttpService.solicitar(entity.id)
                    .subscribe(
                        () => {
                            this.messageService.success('', `Análise da campanha ${entity.nome} solicitada com sucesso`);
                            this.refresh();
                        },
                        (error: ApiError) => {
                            this.messageService.error(error.message, error.cause, error);
                        }
                    );
            }
        });
    }

    /** Realiza a aprovação da análise da campanha */
    aprovarAnalise(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAnaliseAuthGuardService.canEnviar(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0)) {
            this.messageService.info(`Você não possui permissão para aprovar a análise da campanha ${entity?.nome ?? ''}`, 'Sem permissão para aprovar a análise');
            return false;
        }

        this.campanhaEditing = entity;
        this.enviarAnaliseFormModal.aprovar(entity.id);
    }

    /** Realiza a reprovação da análise da campanha */
    reprovarAnalise(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        if (!this.campanhaAnaliseAuthGuardService.canEnviar(entity.id, entity.status, entity.resultado?.quantidadePedidosVendasNaoLiberados ?? 0, entity.resultado?.quantidadePedidosBonificacoesNaoLiberados ?? 0)) {
            this.messageService.info(`Você não possui permissão para reprovar a análise da campanha ${entity?.nome ?? ''}`, 'Sem permissão para reprovar a análise');
            return false;
        }

        this.campanhaEditing = entity;
        this.enviarAnaliseFormModal.reprovar(entity.id);
    }

    /** Após enviar a análise */
    onAfterEnviarAnalise(entity: CampanhaSearch) {

        this.campanhaEditing = entity;
        this.refresh();
    }

    /** Encerra a campanha */
    encerrar(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        if (!this.campanhaEncerramentoAuthGuardService.canEncerrar(entity.id, entity.status)) {
            this.messageService.info(`Você não possui permissão para encerrar a campanha ${entity?.nome ?? ''}`, 'Sem permissão para encerrar a campanha');
            return false;
        }

        this.messageService.confirm(`Ao encerrar a campanha a mesma não poderá ser mais utilizada.\n\nDeseja realmente encerrar a campanha ${entity.nome}?`).then(result => {
            if (result) {
                this.campanhaEncerramentoHttpService.encerrar(entity.id)
                    .subscribe(
                        () => {
                            this.messageService.success('', `Campanha ${entity.nome} encerrada com sucesso`);
                            this.refresh();
                        },
                        (error: ApiError) => {
                            this.messageService.error(error.message, error.cause, error);
                        }
                    );
            }
        });
    }

    /** Abre o modal para liberar os pedidos da campanha */
    liberarPedidos(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }

        this.visualizarModal.open(entity.id, this.filter, campanhaStatusEmAnalise.id, true);
    }

    /** Após liberar pedido */
    afterLiberarPedidos() {
        this.refresh();
    }

    ngOnDestroy() {
        this.searchSubs$?.unsubscribe();
        // Destruindo os badges dos status
        this.clearBadgeStatusContainer();
        // Removendo evento de scroll do body da datatable
        jQuery(this.el.nativeElement).find('.dataTables_scrollBody').off('scroll');
    }
}
